'use client';

import { useActionState, useRef, useState, useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import Turnstile, { useTurnstile } from 'react-turnstile';

import { type FormStoryblok, type RichtextStoryblok } from '@utils/storyblok/storyblok';
import { Alert } from '@common/interaction/Alert';

import { submitAction } from './actions/submitAction';
import { DynamicForm } from './DynamicForm';
import { FormSuccess } from './FormSuccess';
import { createHiddenInput } from './utils/createHiddenInput';
import { createZodSchema, type CreateSchemaTypes } from './utils/createZodSchema';

export type FormState = {
  error?: boolean;
  message: string | undefined;
  success: boolean;
};

type FormValues = Record<string, unknown>;

function createErrorRichText(message: string): RichtextStoryblok {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text: message,
          },
        ],
      },
    ],
  };
}

export function FormBuilder({
  dataLayerEvent,
  errorText,
  fields,
  mailToUser,
  successText,
  ...props
}: FormStoryblok) {
  const [isTransitioning, startTransition] = useTransition();

  const turnstile = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const schemaValidator = createZodSchema(fields as CreateSchemaTypes[]);
  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schemaValidator),
  });

  const [state, formAction, isSubmitting] = useActionState<FormState, any>(submitAction, {
    error: undefined,
    message: undefined,
    success: false,
  });

  const onSubmit = async () => {
    await methods.trigger();

    if (methods.formState.isValid) {
      const formData = new FormData(formRef.current!);
      const values = methods.getValues();

      const dataLayerValues: Record<string, string> = {};

      Object.entries(values).forEach(([key, value]) => {
        const hasDataLayerValue = fields?.find((field) => field.name === key)?.inDatalayer;

        const normalizedValue = Array.isArray(value)
          ? value.join(', ')
          : value instanceof Date
            ? format(value, 'yyyy-MM-dd')
            : value == null
              ? ''
              : String(value);

        if (hasDataLayerValue) {
          dataLayerValues[key] = normalizedValue;
        }

        formData.set(key, normalizedValue);
      });

      if (dataLayerEvent && Object.keys(dataLayerValues).length > 0) {
        if (!window.dataLayer) {
          console.warn(
            'dataLayer is not defined. Ensure Google Tag Manager is properly configured.',
          );
          return;
        }

        window.dataLayer.push({
          event: dataLayerEvent,
          userData: dataLayerValues,
        });
      }

      startTransition(() => {
        const formLabels = Object.entries(values)?.map(([key]) => {
          return {
            name: key,
            label: fields?.find((field) => field.name === key)?.label,
          };
        });

        formAction({
          body: formData,
          labels: formLabels,
          fields,
          mailToUser,
          recaptcha: props.recaptcha,
        });
      });

      if (props.recaptcha) {
        turnstile.reset();
      }

      return;
    }
  };

  if (state.success) {
    return <FormSuccess successText={successText} />;
  }

  const hiddenFields = fields?.filter((field) => field.component === 'FormHiddenField') ?? [];
  const visibleFields = fields?.filter((field) => field.component !== 'FormHiddenField') ?? [];

  const dynamicFields = visibleFields
    ? ([
        ...visibleFields,
        ...hiddenFields.map((field) => {
          return createHiddenInput({
            ...field,
            name: field.name,
            value: field.value,
          });
        }),
        createHiddenInput({
          _uid: `subject`,
          component: 'FormHiddenField',
          inDatalayer: false,
          inUserMail: false,
          name: 'subject',
          value: props.name,
        }),
        props.recaptcha && turnstileToken
          ? createHiddenInput({
              _uid: `turnstile_token`,
              component: 'FormHiddenField',
              inDatalayer: false,
              inUserMail: false,
              name: 'turnstile_token',
              value: turnstileToken,
            })
          : null,
      ].filter(Boolean) as FormStoryblok['fields'])
    : [];

  const alertText = state.message ? createErrorRichText(state.message) : errorText;

  return (
    <>
      {props.recaptcha && (
        <Turnstile
          language="nl-NL"
          onSuccess={(token) => {
            setTurnstileToken(token);
          }}
          sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
        />
      )}
      <DynamicForm
        addColumns={props.addColumns}
        fields={dynamicFields ?? []}
        formName={props.name}
        formRef={formRef}
        isPending={isTransitioning || isSubmitting}
        methods={methods}
        onSubmit={methods.handleSubmit(onSubmit)}
        submitButtonText={props.submitButtonText}
      >
        {state.error && alertText && (
          <Alert
            severity="error"
            text={alertText}
          />
        )}
      </DynamicForm>
    </>
  );
}
